import { format, differenceInCalendarDays } from "date-fns";
import {
  formatPositionValue,
  formatTypeOfTender,
  formatLeagueTier,
} from "../constants/agentTenderChoices";
import { formatCurrencyRange } from "./tenderFormatters";
import { formatCurrencyWithSymbol } from "./currencyFormatter";

// Shared formatting for N3 Trusted-Agent search requests. Reads the backend's
// canonical field names (agent_search_serializers._SPEC_FIELDS): position_primary,
// position_secondary (single), age_min/age_max, transfer_type, window,
// level_expectation, additional_details, and the FLAT terms fields
// (terms_enabled + salary_*/commission_*). Used by both the club Section-Home and
// the agent Inbox so the two never drift.

const PERIOD_LABEL = { per_year: "year", per_month: "month", per_week: "week" };
const BASIS_LABEL = {
  percentage_of_salary: "of player salary",
  percentage_of_transfer: "of transfer fee",
  fixed_fee: "fixed",
};

// "Left Winger, 18–24" for a player, or the staff role ("Sporting Director") for
// a staff request (DoD 30) — the backend sends staff_role_display. Falls back to
// a generic label so a card is never a bare blank.
export const formatRequestTitle = (req) => {
  const isStaff = req?.tender_category === "staff" || !!req?.staff_role;
  const base = isStaff
    ? req.staff_role_display || "Staff role"
    : formatPositionValue(req.position_primary) || "Player search";
  const age = req.age_min && req.age_max ? `, ${req.age_min}–${req.age_max}` : "";
  return `${base}${age}`;
};

// Absolute "12 May 2026" for attribution lines (created/closed by, DoD 27).
export const formatAbsoluteDate = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  return format(d, "d MMM yyyy");
};

// "12 May, 14:30" for activity-trail events (DoD 28).
export const formatEventTime = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  return format(d, "d MMM, HH:mm");
};

// Relative rendering for any timestamp: recent reads relatively
// ("today"/"yesterday"/"3 days ago"); older falls back to an absolute "d MMM"
// (with year when it isn't the current year). Shared by the recipients row
// ("Sent 12 May to:", N3·4) and the agent inbox lines ("sent 12 May",
// "accepted 3 days ago", N3·6/M7 — Redesign 2.0).
export const formatRelativeDate = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const days = differenceInCalendarDays(new Date(), d);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return format(d, sameYear ? "d MMM" : "d MMM yyyy");
};

// Compact "sent" date for the recipients row (design N3·4 "Sent 12 May to:").
export const formatSentDate = (req) => formatRelativeDate(req?.created_at);

// B8 answer-deadline banner (Commissioned Search, Annex B8/B9). The backend
// sends the DERIVED remaining seconds (never persisted); the FE renders the
// human countdown ("3 days and 4 hours", "5 hours", "12 minutes"). The caller
// composes the countdown WITH its " left" tail into ONE text node
// (`${countdown} left`), which the DOM static-translation layer matches against
// the per-variant bundle keys in commissioned-search.json ("{d} days and {h}
// hours left", "{n} hours left", "1 minute left", …) — the single-largest-unit
// day cases reuse transfer-windows' "{n} days left" / "1 day left". Returns ""
// for a null remaining (no deadline set — "3/5/7 days or none", B9). Clamps at
// 0 — an expired invitation lives in the Archive bucket, not the banner.
const _unit = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
export const formatDeadlineCountdown = (seconds) => {
  if (seconds == null) return "";
  const s = Math.max(0, Math.floor(seconds));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  if (days > 0) {
    return hours > 0 ? `${_unit(days, "day")} and ${_unit(hours, "hour")}` : _unit(days, "day");
  }
  if (hours > 0) {
    return minutes > 0
      ? `${_unit(hours, "hour")} and ${_unit(minutes, "minute")}`
      : _unit(hours, "hour");
  }
  return _unit(Math.max(1, minutes), "minute");
};

// A1 chain: "since 12 May · no expiry" for a winner's Searching chip — the live
// commissioned request has no answer deadline (Annex A1/A5 "no expiry while
// searching"). `searching_since` is the ISO moment the request went up.
export const formatSearchingSince = (searchingSince) => {
  const d = formatAbsoluteDate(searchingSince);
  return d ? `since ${d} · no expiry` : "no expiry";
};

// A recipient the club still holds the commission with — accepted through the
// end states downstream of it. The A1 "winner" row (or the still-live one).
const WINNER_STATES = ["accepted", "searching", "delivered", "nothing_found"];
export const isWinnerRecipient = (recipient) =>
  WINNER_STATES.includes(recipient?.status);

// A request where an agent already holds the commission (A4: close, not
// withdraw). Prefers the backend's own `winner`, falls back to a recipient scan
// so the card is correct even from a lean list payload.
export const requestHasWinner = (req) =>
  !!req?.winner || (req?.recipients || []).some(isWinnerRecipient);

// A1 pending-only request: nobody has accepted and it is not closed — the state
// where Withdraw (A14b) is offered instead of Close (A4). "Nothing runs in the
// network until then."
export const isPendingOnly = (req) =>
  req?.status !== "closed" &&
  !requestHasWinner(req) &&
  (req?.recipients || []).some((r) => r.status === "sent" || r.status === "seen");

// Single optional secondary position (backend allows one).
export const formatSecondary = (req) =>
  req.position_secondary ? formatPositionValue(req.position_secondary) : "";

// "Permanent · Summer 2026 · 1st tier level · <details>"
export const formatRequestMeta = (req) => {
  const parts = [
    req.transfer_type ? formatTypeOfTender(req.transfer_type) : null,
    // Professional / Youth level (Part B two-axis model): shown for BOTH players
    // AND staff now — every request carries a recruiting level.
    req.recruiting_for_display || null,
    req.window || null,
    req.level_expectation ? `${formatLeagueTier(req.level_expectation)} level` : null,
    (req.additional_details || "").trim() || null,
  ].filter(Boolean);
  return parts.join(" · ");
};

// Market value for display (DoD 21) — a currency range, a fixed figure, or null
// when not specified / not important. N3 is not anonymous, so real figures show
// (unlike the N2 budget band).
export const formatMarketValue = (req) => {
  if (!req) return null;
  const cur = req.currency || "EUR";
  if (req.market_value_type === "market_value_range") {
    return formatCurrencyRange(req.market_value_from, req.market_value_to, cur);
  }
  if (req.market_value_type === "market_value_fixed" && req.fix_market_value) {
    return formatCurrencyWithSymbol(req.fix_market_value, cur);
  }
  return null;
};

// Maximum transfer fee for display (DoD 20) — null when unset or when the request
// is free-agents-only (that state is shown separately).
export const formatMaxTransferFee = (req) => {
  if (!req || !req.maximum_transfer_fee) return null;
  return formatCurrencyWithSymbol(req.maximum_transfer_fee, req.currency || "EUR");
};

const shortMoney = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n >= 1000) return `€${Math.round(n / 1000)}k`;
  return `€${n}`;
};

// "€180k/year salary · 10% commission of player salary" from the FLAT term
// fields on the request — or null when terms are off/absent.
export const formatTerms = (req) => {
  if (!req || !req.terms_enabled) return null;
  const bits = [];
  const salary = shortMoney(req.salary_amount);
  if (salary) {
    bits.push(`${salary}/${PERIOD_LABEL[req.salary_period] || "year"} salary`);
  }
  if (req.commission_value) {
    if (req.commission_basis === "fixed_fee") {
      // Fixed fee is a money amount + currency, never a percentage.
      const amount = formatCurrencyWithSymbol(req.commission_value, req.currency || "EUR");
      bits.push(`${amount} commission (fixed)`);
    } else {
      // Staff requests have no "player" — show "of salary" instead (DoD 30).
      const isStaff = req.tender_category === "staff";
      const basis =
        isStaff && req.commission_basis === "percentage_of_salary"
          ? "of salary"
          : BASIS_LABEL[req.commission_basis] || "";
      bits.push(`${req.commission_value}% commission ${basis}`.trim());
    }
  }
  return bits.length ? bits.join(" · ") : null;
};

// Abbreviated terms line for the 390px mobile boards — agent-inbox card
// (design M7: "€180k/yr · 10% commission", no prefix) and club search-request
// card (design M4, rendered with a "Terms: " prefix by the card). Pure display
// variant of formatTerms over the SAME flat term fields — reuses shortMoney
// byte-identical (including its hardcoded "€" quirk, see FUNCTION-TODO).
// Desktop keeps the long formatTerms form. ONE shared export for B3+B4
// (DECISIONS D-B3-4).
const SHORT_PERIOD_LABEL = { per_year: "yr", per_month: "mo", per_week: "wk" };
export const formatTermsShort = (req) => {
  if (!req || !req.terms_enabled) return null;
  const bits = [];
  const salary = shortMoney(req.salary_amount);
  if (salary) {
    bits.push(`${salary}/${SHORT_PERIOD_LABEL[req.salary_period] || "yr"}`);
  }
  if (req.commission_value) {
    if (req.commission_basis === "fixed_fee") {
      const amount = formatCurrencyWithSymbol(req.commission_value, req.currency || "EUR");
      bits.push(`${amount} commission`);
    } else {
      bits.push(`${req.commission_value}% commission`);
    }
  }
  return bits.length ? bits.join(" · ") : null;
};
